import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell, Stat } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Phone, Upload, FileText, Sparkles, Search, Download } from 'lucide-react';
import RangeChips from '@/components/RangeChips';
import CallLogDialog from '@/components/CallLogDialog';
import { useAuth } from '@/context/AuthContext';

const STATUS = ['sourced', 'lineup', 'interview', 'selected', 'offered', 'joined', 'rejected'];
const STATUS_LABEL = {
  sourced: 'New',
  lineup: 'In Progress',
  interview: 'Interview Scheduled',
  selected: 'Selected',
  offered: 'Offered',
  joined: 'Joined',
  rejected: 'Rejected',
};
const STATUS_COLOR = {
  sourced: 'bg-zinc-500/15 text-zinc-600 border-zinc-500/30',
  lineup: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  interview: 'bg-purple-500/15 text-purple-600 border-purple-500/30',
  selected: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  offered: 'bg-primary/15 text-primary border-primary/30',
  joined: 'bg-emerald-600/20 text-emerald-700 border-emerald-600/40',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
};
const OUTCOME_COLOR = {
  connected: 'text-emerald-600 border-emerald-500/40 bg-emerald-500/10',
  not_answered: 'text-amber-600 border-amber-500/40 bg-amber-500/10',
  callback: 'text-blue-600 border-blue-500/40 bg-blue-500/10',
  not_interested: 'text-red-600 border-red-500/40 bg-red-500/10',
};
const OUTCOME_LABEL = { connected: 'Connected', not_answered: 'Not Ans.', callback: 'Callback', not_interested: 'Not Int.' };

const EMPTY = {
  name: '', email: '', phone: '', current_company: '', experience: '',
  ctc: '', ectc: '', notice_period: '', source_portal: 'Naukri',
  job_id: '', client_name: '', status: 'sourced', notes: '',
  resume_path: '', resume_filename: '', skills: '', education: '', location: '',
  designation: '', alternate_phone: '', full_address: '', linkedin_url: '',
  portfolio_url: '', github_url: '', professional_summary: '',
  relevant_experience: '', employment_type: '', preferred_location: '',
  certifications: '', languages: '', projects: '', achievements: '',
  work_history: [], lineup_datetime: '', next_follow_up: '',
};

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) { return v ? new Date(v).toISOString() : ''; }
function fmt(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } }
function parsedText(value) { return Array.isArray(value) ? value.join(', ') : value || ''; }

export default function Lineups() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canFilter = ['admin', 'manager'].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [portals, setPortals] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [parsing, setParsing] = useState(false);
  const [q, setQ] = useState('');
  const cvRef = useRef(null);
  const [rangeKey, setRangeKey] = useState('all');
  const [range, setRange] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [callFor, setCallFor] = useState(null);
  const [callOpen, setCallOpen] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (range) { params.from_date = range.from; params.to_date = range.to; }
      if (canFilter && ownerFilter !== 'all') params.owner_id = ownerFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const [{ data: cs }, { data: logs }] = await Promise.all([
        api.get('/candidates', { params }),
        api.get('/call-logs', { params: { candidate_type: 'candidate', ...(range ? { from_date: range.from, to_date: range.to } : {}) } }),
      ]);
      setRows(cs); setCallLogs(logs);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeKey, range, ownerFilter, statusFilter]);
  useEffect(() => {
    api.get('/jobs').then(({ data }) => setJobs(data)).catch(() => {});
    api.get('/portals').then(({ data }) => setPortals(data)).catch(() => {});
    if (canFilter) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
  }, [canFilter]);

  // Map candidate_id → most recent call outcome
  const lastOutcomeMap = useMemo(() => {
    const m = {};
    for (const l of callLogs) {
      if (l.candidate_type !== 'candidate') continue;
      const cur = m[l.candidate_id];
      if (!cur || (l.started_at || '') > (cur.started_at || '')) m[l.candidate_id] = l;
    }
    return m;
  }, [callLogs]);

  const parseCv = async (file) => {
    if (!file) return; setParsing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/candidates/upload-cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const p = data.parsed || {};
      setForm(f => ({
        ...f,
        resume_path: data.resume_path || f.resume_path,
        resume_filename: data.resume_path ? data.resume_filename : f.resume_filename,
        name: p.name || f.name, email: p.email || f.email, phone: p.phone || f.phone,
        current_company: p.current_company || f.current_company, experience: p.experience || f.experience,
        ctc: p.ctc || f.ctc, ectc: p.ectc || f.ectc, notice_period: p.notice_period || f.notice_period,
        skills: parsedText(p.skills) || f.skills, education: parsedText(p.education) || f.education,
        certifications: parsedText(p.certifications) || f.certifications,
        languages: parsedText(p.languages) || f.languages,
        projects: parsedText(p.projects) || f.projects,
        achievements: parsedText(p.achievements) || f.achievements,
        location: p.location || f.location, alternate_phone: p.alternate_phone || f.alternate_phone,
        full_address: p.full_address || f.full_address, linkedin_url: p.linkedin_url || f.linkedin_url,
        portfolio_url: p.portfolio_url || f.portfolio_url, github_url: p.github_url || f.github_url,
        professional_summary: p.professional_summary || f.professional_summary,
        relevant_experience: p.relevant_experience || f.relevant_experience,
        employment_type: p.employment_type || f.employment_type,
        preferred_location: p.preferred_location || f.preferred_location,
        work_history: Array.isArray(p.work_history) ? p.work_history : f.work_history,
        designation: p.current_role || f.designation,
      }));
      if (data.storage_warning) {
        toast.warning(`${data.storage_warning} Parsed details are ready to review.`);
      } else {
        toast.success(Object.keys(p).length ? 'Full CV profile parsed — review before saving' : 'CV uploaded (fill manually)');
      }
    } catch (e) { toast.error(formatErr(e)); } finally { setParsing(false); }
  };

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    try {
      const job = jobs.find(j => j.id === form.job_id);
      const client_name = form.client_name || (job ? job.client : '');
      await api.post('/candidates', {
        ...form,
        client_name,
        job_id: form.job_id || null,
        lineup_datetime: form.lineup_datetime || (form.status === 'lineup' ? new Date().toISOString() : null),
      });
      toast.success('Record added'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setFollowUp = async (c, iso) => {
    try {
      await api.patch(`/candidates/${c.id}`, { ...c, next_follow_up: iso || null });
      toast.success('Follow-up set'); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openCall = (e, c) => { e.stopPropagation(); setCallFor({ id: c.id, name: c.name, phone: c.phone }); setCallOpen(true); };

  const exportExcel = async () => {
    try {
      const res = await api.get('/candidates/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `shrs-lineups-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = rows.filter(r => (r.name + ' ' + (r.phone || '') + ' ' + (r.email || '') + ' ' + (r.current_company || '')).toLowerCase().includes(q.toLowerCase()));

  return (
    <PageShell>
      <PageHeader
        eyebrow="Pipeline"
        title="Lineups"
        subtitle="All candidates in one unified pipeline — from sourced to joined. Tap any row for full profile & call history."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={exportExcel} data-testid="lineups-export" className="h-11"><Download className="w-4 h-4 mr-2" />Export</Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-lineup-btn" className="h-11 bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" />New</Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-2xl h-[92vh] sm:h-auto sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl sm:text-2xl font-black">
                    Add to lineups
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Upload a CV to prefill candidate details, then review and save the lineup.
                  </DialogDescription>
                </DialogHeader>

                <div className="tactical-card p-4 bg-primary/5 border-primary/30">
                  <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-primary" /><div className="font-display text-sm font-black uppercase tracking-widest">AI CV import (optional)</div></div>
                  <input ref={cvRef} type="file" data-testid="lineup-cv-file" className="hidden" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={(e) => parseCv(e.target.files?.[0])} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button type="button" onClick={() => cvRef.current?.click()} disabled={parsing} className="h-11 bg-primary hover:bg-primary/90 text-white"><Upload className="w-4 h-4 mr-2" />{parsing ? 'Parsing…' : 'Upload CV'}</Button>
                    {form.resume_filename && <div className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" />{form.resume_filename}</div>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2"><Label>Name *</Label><Input data-testid="lineup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" /></div>
                  <div><Label>Phone</Label><Input data-testid="lineup-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" /></div>
                  <div><Label>Email</Label><Input type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" /></div>
                  <div><Label>Current role</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="h-11" /></div>
                  <div><Label>Current company</Label><Input value={form.current_company} onChange={(e) => setForm({ ...form, current_company: e.target.value })} className="h-11" /></div>
                  <div><Label>Experience</Label><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="h-11" /></div>
                  <div><Label>Notice period</Label><Input value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })} className="h-11" /></div>
                  <div><Label>Current CTC</Label><Input value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} className="h-11" /></div>
                  <div><Label>Expected CTC</Label><Input value={form.ectc} onChange={(e) => setForm({ ...form, ectc: e.target.value })} className="h-11" /></div>
                  <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11" /></div>
                  <div><Label>Preferred location</Label><Input data-testid="lineup-preferred-location" value={form.preferred_location} onChange={(e) => setForm({ ...form, preferred_location: e.target.value })} className="h-11" /></div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="lineup-status" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Source portal</Label>
                    <Select value={form.source_portal} onValueChange={(v) => setForm({ ...form, source_portal: v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>{portals.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}<SelectItem value="Referral">Referral</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Job opening</Label>
                    <Select value={form.job_id || 'none'} onValueChange={(v) => setForm({ ...form, job_id: v === 'none' ? '' : v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">None</SelectItem>{jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title} · {j.client}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Client name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-11" placeholder="Auto-filled from job" /></div>
                  <div><Label>Added at (optional)</Label><Input type="datetime-local" data-testid="lineup-datetime" value={isoToLocalInput(form.lineup_datetime)} onChange={(e) => setForm({ ...form, lineup_datetime: localInputToIso(e.target.value) })} className="h-11" /></div>
                  <div><Label>Next follow-up</Label><Input type="datetime-local" data-testid="lineup-followup" value={isoToLocalInput(form.next_follow_up)} onChange={(e) => setForm({ ...form, next_follow_up: localInputToIso(e.target.value) })} className="h-11" /></div>
                  <div className="sm:col-span-2"><Label>Skills</Label><Textarea rows={2} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Professional summary</Label><Textarea data-testid="lineup-professional-summary" rows={3} value={form.professional_summary} onChange={(e) => setForm({ ...form, professional_summary: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>

                <div className="sticky bottom-0 bg-card pt-3 -mx-4 sm:mx-0 px-4 sm:px-0">
                  <Button data-testid="lineup-save" onClick={save} className="w-full h-12 bg-primary hover:bg-primary/90 text-white">Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Filters */}
      <div className="tactical-card p-3 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="lineups-search" placeholder="Search name, phone, email, company…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-40 h-11" data-testid="lineups-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>)}
          </SelectContent>
        </Select>
        {canFilter && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-full md:w-52 h-11" data-testid="lineups-owner-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All recruiters</SelectItem>
              {users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="tactical-card p-3 mb-4">
        <RangeChips value={rangeKey} onChange={(k, r) => { setRangeKey(k); setRange(r); }} testidPrefix="lineups-range" />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat testid="lineups-total" label="Total" value={rows.length} hint="In filter" accent />
        <Stat testid="lineups-today" label="Follow-ups today" value={rows.filter(r => r.next_follow_up && new Date(r.next_follow_up).toDateString() === new Date().toDateString()).length} hint="Due today" />
        <Stat testid="lineups-overdue" label="Overdue" value={rows.filter(r => r.next_follow_up && new Date(r.next_follow_up) < new Date() && new Date(r.next_follow_up).toDateString() !== new Date().toDateString()).length} hint="Past due" />
      </div>

      {/* MOBILE cards */}
      <div className="md:hidden space-y-3" data-testid="lineups-cards">
        {filtered.length === 0 && <div className="tactical-card p-6 text-center text-sm text-muted-foreground">No records. Tap "New" to add one.</div>}
        {filtered.map(c => {
          const last = lastOutcomeMap[c.id];
          const follow = c.next_follow_up ? new Date(c.next_follow_up) : null;
          const overdue = follow && follow < new Date();
          return (
            <div key={c.id} className="tactical-card p-4 active:bg-accent/30 transition-colors cursor-pointer" data-testid={`lineup-card-${c.id}`} onClick={() => nav(`/lineups/${c.id}`)}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg font-black tracking-tight truncate">{c.name}</div>
                    <Badge variant="outline" className={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status] || c.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{c.designation || c.current_company || '—'}</div>
                </div>
                {c.phone && <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs font-mono text-primary hover:underline flex-shrink-0">{c.phone}</a>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
                <div><div className="uppercase tracking-widest text-muted-foreground">Added</div><div className="font-medium">{fmt(c.lineup_datetime || c.created_at)}</div></div>
                <div><div className="uppercase tracking-widest text-muted-foreground">Follow-up</div><div className={overdue ? 'font-medium text-red-600' : 'font-medium'}>{c.next_follow_up ? fmt(c.next_follow_up) : '—'}</div></div>
              </div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-[11px]">
                  <span className="uppercase tracking-widest text-muted-foreground mr-1">Last call:</span>
                  {last ? <Badge variant="outline" className={OUTCOME_COLOR[last.outcome]}>{OUTCOME_LABEL[last.outcome]}</Badge> : <span className="text-muted-foreground">—</span>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">by {c.owner_name}</div>
              </div>
              <Button onClick={(e) => openCall(e, c)} data-testid={`lineup-call-${c.id}`} className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white">
                <Phone className="w-4 h-4 mr-2" /> Call &amp; log
              </Button>
            </div>
          );
        })}
      </div>

      {/* DESKTOP table */}
      <div className="hidden md:block tactical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Current role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Added</TableHead>
              <TableHead>Next follow-up</TableHead>
              <TableHead>Last call</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="lineups-tbody">
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No records in this filter.</TableCell></TableRow>}
            {filtered.map(c => {
              const last = lastOutcomeMap[c.id];
              const follow = c.next_follow_up ? new Date(c.next_follow_up) : null;
              const overdue = follow && follow < new Date();
              return (
                <TableRow key={c.id} data-testid={`lineup-row-${c.id}`} className="cursor-pointer hover:bg-accent/40" onClick={() => nav(`/lineups/${c.id}`)}>
                  <TableCell className="font-medium">{c.name}<div className="text-[10px] uppercase tracking-widest text-muted-foreground">by {c.owner_name}</div></TableCell>
                  <TableCell>{c.phone ? <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline font-mono text-sm">{c.phone}</a> : '—'}</TableCell>
                  <TableCell className="text-sm">{c.email || '—'}</TableCell>
                  <TableCell className="text-sm">{c.designation || c.current_company || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status] || c.status}</Badge></TableCell>
                  <TableCell className="text-xs">{fmt(c.lineup_datetime || c.created_at)}</TableCell>
                  <TableCell>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Input type="datetime-local" className="w-48 h-8 text-xs"
                        value={isoToLocalInput(c.next_follow_up)}
                        onChange={(e) => setFollowUp(c, localInputToIso(e.target.value))} />
                      {follow && <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${overdue ? 'text-red-600' : 'text-blue-600'}`}>{overdue ? 'overdue' : 'scheduled'}</div>}
                    </div>
                  </TableCell>
                  <TableCell>{last ? <Badge variant="outline" className={OUTCOME_COLOR[last.outcome]}>{OUTCOME_LABEL[last.outcome]}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={(e) => openCall(e, c)} className="bg-primary hover:bg-primary/90 text-white"><Phone className="w-3.5 h-3.5 mr-1" />Call</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CallLogDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        candidate={callFor}
        candidateType="candidate"
        defaultDialer
        onSaved={() => { setCallOpen(false); load(); }}
      />
    </PageShell>
  );
}
